/**
 * VoiceSelectionModal — modal forcing the writer to pick a narration
 * voice on the very first text episode of a series. Once confirmed,
 * the chosen voice is LOCKED to the series and used for every future
 * text episode (enforced server-side; this modal also never re-opens).
 *
 * Props:
 *   • open            boolean
 *   • onCancel()      close without picking
 *   • onConfirm(voice) commit a voice — caller proceeds with publish
 *   • busy            disables interaction while publish is in flight
 *
 * Renders the live voice list from `/api/tts/voices`, with the
 * existing `<VoicePreviewButton />` on every option so the writer can
 * audition before locking. Confirmation is intentionally a two-step
 * (select → confirm with checkmark) so a slip-tap can't accidentally
 * bind the wrong voice for the rest of the series.
 */
import React, { useEffect, useState } from 'react';
import { X, Lock, Loader2, CheckCircle2, AlertTriangle, Mic2 } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import VoicePreviewButton from './VoicePreviewButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const VoiceSelectionModal = ({ open, onCancel, onConfirm, busy = false }) => {
  const [voices, setVoices] = useState([]);
  const [picked, setPicked] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setPicked(null);
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/tts/voices`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (cancelled) return;
        const list = (Array.isArray(d.voices) ? d.voices : [])
          .map((v) => (typeof v === 'string' ? v : (v.id || v.name || 'default')))
          // "default" is a placeholder fallback — it's not a real voice
          // so we strip it. If the upstream is offline we'll show the
          // banner below and the writer can retry.
          .filter((v) => v && v !== 'default');
        setVoices(list);
        setOffline(Boolean(d.upstream_offline));
      } catch (e) {
        if (!cancelled) setErr(e.message || 'Voice list unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget && !busy) onCancel?.();
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onBackdrop}
      data-testid="voice-selection-modal"
    >
      <div className="w-full max-w-lg rounded-2xl border border-fuchsia-500/40 bg-slate-950 shadow-2xl shadow-fuchsia-900/30 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 bg-gradient-to-r from-fuchsia-950/40 to-slate-900">
          <div className="flex items-center gap-2">
            <Mic2 className="w-5 h-5 text-fuchsia-300" />
            <h3 className="text-white font-black text-sm tracking-wide">
              Pick this series&apos;s narration voice
            </h3>
          </div>
          <button
            type="button"
            onClick={() => !busy && onCancel?.()}
            disabled={busy}
            className="text-slate-400 hover:text-white p-1 rounded disabled:opacity-50"
            data-testid="voice-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="rounded-md border border-amber-500/40 bg-amber-950/30 px-3 py-2 text-[12px] text-amber-200 flex items-start gap-2">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Once locked, this voice narrates <strong>every text episode</strong> of this
              series. It can&apos;t be changed later. Preview before you pick.
            </span>
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-4 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading voices…
            </div>
          )}

          {err && !loading && (
            <div className="rounded-md border border-rose-500/40 bg-rose-950/30 px-3 py-2 text-xs text-rose-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              {err}
            </div>
          )}

          {offline && !loading && (
            <div className="rounded-md border border-rose-500/40 bg-rose-950/30 px-3 py-2 text-xs text-rose-200">
              The narration service is offline — your tunnel is unreachable.
              Restart cloudflared and try again.
            </div>
          )}

          {!loading && !err && voices.length === 0 && !offline && (
            <div className="rounded-md border border-slate-700 bg-slate-900/60 px-3 py-3 text-xs text-slate-300 text-center">
              No custom voices found on the narration server.
            </div>
          )}

          {!loading && voices.length > 0 && (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="voice-modal-list">
              {voices.map((v) => {
                const active = picked === v;
                return (
                  <div
                    key={v}
                    className={`flex items-center gap-2 rounded-xl border p-2.5 transition ${
                      active
                        ? 'border-fuchsia-400 bg-fuchsia-500/15 shadow-[0_0_0_1px_rgba(217,70,239,0.4)]'
                        : 'border-slate-700 bg-slate-900/40 hover:border-fuchsia-400/60'
                    }`}
                    data-testid={`voice-modal-row-${v.replace(/\s+/g, '-')}`}
                  >
                    <button
                      type="button"
                      onClick={() => setPicked(v)}
                      disabled={busy}
                      className={`flex-1 flex items-center gap-2 text-left text-sm font-bold px-2 py-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-300 ${
                        active ? 'text-white' : 'text-slate-200'
                      }`}
                    >
                      {active ? (
                        <CheckCircle2 className="w-4 h-4 text-fuchsia-300" />
                      ) : (
                        <span className="w-4 h-4 rounded-full border border-slate-500" />
                      )}
                      <span className="truncate">{v}</span>
                    </button>
                    <VoicePreviewButton voice={v} size="sm" />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-800 bg-slate-950/80">
          <button
            type="button"
            onClick={() => !busy && onCancel?.()}
            disabled={busy}
            className="text-sm font-bold text-slate-300 hover:text-white px-3 py-1.5 rounded-md disabled:opacity-50"
            data-testid="voice-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => picked && onConfirm?.(picked)}
            disabled={!picked || busy}
            className="inline-flex items-center gap-1.5 text-sm font-black bg-gradient-to-r from-fuchsia-500 to-purple-500 hover:from-fuchsia-400 hover:to-purple-400 text-white px-4 py-2 rounded-md disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-300"
            data-testid="voice-modal-confirm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {picked ? `Lock "${picked}" & publish` : 'Pick a voice'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoiceSelectionModal;
